import {
  IUserSubscriptionApiCall,
  IRestApiService,
  SubscriptionPaymentLinkResponse,
  TariffPlansResponse,
  DetailedSubscriptionDataResponse,
  SubscriptionDataResponse,
  SubscriptionEditRequest,
} from '../types';
import { UserSubscriptionTracker } from '../services/analytics/apiTrackers';

// The backend's tariff-plans catalog stores pricing nested as `pricing: { month, threeMonth, year }`
// (see billing-system/tariffs/tariff-pricing.types.ts) - it does not send the flat
// pricePerMonth/pricePerYear/pricePerThreeMonth fields TariffPlansResponse's items declare.
interface RawTariffPricing {
  month: number;
  threeMonth: number;
  year: number;
}
interface RawTariffPlan {
  label: string;
  countOfClients: number;
  pricing: RawTariffPricing;
}
interface RawAddonPlan {
  label: string;
  pricing: RawTariffPricing;
}
interface RawTariffPlansResponse {
  tariffPlans: RawTariffPlan[];
  addons: RawAddonPlan[];
}

class UserSubscriptionApiCall implements IUserSubscriptionApiCall {
  private restApiService: IRestApiService;

  constructor(restApiService: IRestApiService) {
    this.restApiService = restApiService;
  }
  getLinkForSubscriptionPayment(ownerId: string, subscriptionData: any): Promise<SubscriptionPaymentLinkResponse> {
    const startTime = Date.now();

    return this.restApiService
      .post<SubscriptionPaymentLinkResponse>(`user-subscription/owner/${ownerId}`, subscriptionData)
      .then((result) => {
        UserSubscriptionTracker.trackGetLinkForSubscriptionPayment({
          ownerId,
          subscriptionData,
          success: true,
          duration: Date.now() - startTime,
          result,
        });
        return result;
      })
      .catch((error) => {
        UserSubscriptionTracker.trackGetLinkForSubscriptionPayment({
          ownerId,
          subscriptionData,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  getTariffPlans(): Promise<TariffPlansResponse> {
    const startTime = Date.now();

    return this.restApiService
      .get<RawTariffPlansResponse>(`user-subscription/tariff-plans`)
      .then((result) => {
        const mapped: TariffPlansResponse = {
          tariffPlans: result.tariffPlans.map((tariff) => ({
            label: tariff.label,
            countOfClients: tariff.countOfClients,
            pricePerMonth: tariff.pricing.month,
            pricePerYear: tariff.pricing.year,
            pricePerThreeMonth: tariff.pricing.threeMonth,
          })),
          addons: result.addons.map((addon) => ({
            label: addon.label,
            pricePerMonth: addon.pricing.month,
            pricePerYear: addon.pricing.year,
          })),
        };
        UserSubscriptionTracker.trackGetTariffPlans({
          success: true,
          duration: Date.now() - startTime,
          result: mapped,
        });
        return mapped;
      })
      .catch((error) => {
        UserSubscriptionTracker.trackGetTariffPlans({
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  getDetailedSubscriptionData(ownerId: string): Promise<DetailedSubscriptionDataResponse> {
    const startTime = Date.now();

    return this.restApiService
      .get<DetailedSubscriptionDataResponse>(`user-subscription/owner/${ownerId}/detailed`)
      .then((result) => {
        UserSubscriptionTracker.trackGetDetailedSubscriptionData({
          ownerId,
          success: true,
          duration: Date.now() - startTime,
          result,
        });
        return result;
      })
      .catch((error) => {
        UserSubscriptionTracker.trackGetDetailedSubscriptionData({
          ownerId,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  getSubscriptionData(ownerId: string): Promise<SubscriptionDataResponse> {
    const startTime = Date.now();

    return this.restApiService
      .get<SubscriptionDataResponse>(`user-subscription/owner/${ownerId}`)
      .then((result) => {
        UserSubscriptionTracker.trackGetSubscriptionData({
          ownerId,
          success: true,
          duration: Date.now() - startTime,
          result,
        });
        return result;
      })
      .catch((error) => {
        UserSubscriptionTracker.trackGetSubscriptionData({
          ownerId,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  editSubscription(
    ownerId: string,
    subscriptionData: {
      tariff: { label: string; billingPeriod: string };
      addons: (
        | { label: string; billingPeriod: string; active: boolean }
        | {
            label: string;
            billingPeriod: string;
            active: boolean;
          }
      )[];
    }
  ): Promise<any> {
    const startTime = Date.now();

    return this.restApiService
      .put(`user-subscription/owner/${ownerId}`, subscriptionData)
      .then((result) => {
        UserSubscriptionTracker.trackEditSubscription({
          ownerId,
          subscriptionData,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        UserSubscriptionTracker.trackEditSubscription({
          ownerId,
          subscriptionData,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  cancelSubscription(ownerId: string): Promise<any> {
    const startTime = Date.now();

    return this.restApiService
      .post(`user-subscription/owner/${ownerId}/cancel`)
      .then((result) => {
        UserSubscriptionTracker.trackCancelSubscription({
          ownerId,
          success: true,
          duration: Date.now() - startTime,
          result,
        });
        return result;
      })
      .catch((error) => {
        UserSubscriptionTracker.trackCancelSubscription({
          ownerId,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  resumeSubscription(ownerId: string): Promise<any> {
    const startTime = Date.now();

    return this.restApiService
      .post(`user-subscription/owner/${ownerId}/resume`)
      .then((result) => {
        UserSubscriptionTracker.trackResumeSubscription({
          ownerId,
          success: true,
          duration: Date.now() - startTime,
          result,
        });
        return result;
      })
      .catch((error) => {
        UserSubscriptionTracker.trackResumeSubscription({
          ownerId,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  payInvoice(ownerId: string, invoiceId: string): Promise<any> {
    const startTime = Date.now();

    return this.restApiService
      .post(`user-subscription/owner/${ownerId}/invoice/${invoiceId}/pay`)
      .then((result) => {
        UserSubscriptionTracker.trackPayInvoice({
          ownerId,
          invoiceId,
          success: true,
          duration: Date.now() - startTime,
          result,
        });
        return result;
      })
      .catch((error) => {
        UserSubscriptionTracker.trackPayInvoice({
          ownerId,
          invoiceId,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  getSubscriptionInfo(ownerId: string): Promise<any> {
    const startTime = Date.now();

    return this.restApiService
      .get(`user-subscription/owner/${ownerId}/subscription-info`)
      .then((result) => {
        UserSubscriptionTracker.trackGetSubscriptionInfo({
          ownerId,
          success: true,
          duration: Date.now() - startTime,
          result,
        });
        return result;
      })
      .catch((error) => {
        UserSubscriptionTracker.trackGetSubscriptionInfo({
          ownerId,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  getUniqueClientsCount(ownerId: string, dateFrom: string, dateTo: string): Promise<{ clientCount: number }> {
    return this.restApiService.post<{ clientCount: number }>(`user-subscription/owner/${ownerId}/find-uniq-clients`, {
      dateFrom,
      dateTo,
    });
  }

  removeAddon(ownerId: string, addonLabel: string): Promise<any> {
    const startTime = Date.now();

    return this.restApiService
      .delete(`user-subscription/owner/${ownerId}/addon/${addonLabel}`)
      .then((result) => {
        UserSubscriptionTracker.trackRemoveAddon({
          ownerId,
          addonLabel,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        UserSubscriptionTracker.trackRemoveAddon({
          ownerId,
          addonLabel,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }
}

export default UserSubscriptionApiCall;
